import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import './ProjectSelection.css';

function ProjectSelection() {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [showEditProjectForm, setShowEditProjectForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects/');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setLoading(true);
    try {
      await axios.post('/api/projects/', { name: newProjectName });
      setNewProjectName('');
      setShowNewProjectForm(false);
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setEditProjectName(project.name);
    setShowEditProjectForm(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!editProjectName.trim()) return;

    setLoading(true);
    try {
      await axios.put(`/api/projects/${editingProject.id}`, { name: editProjectName });
      setEditingProject(null);
      setEditProjectName('');
      setShowEditProjectForm(false);
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (project) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el proyecto "${project.name}"?`)) {
      try {
        await axios.delete(`/api/projects/${project.id}`);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error al eliminar el proyecto');
      }
    }
  };

  const handleSelectProject = (project) => {
    localStorage.setItem('currentProject', JSON.stringify(project));
    navigate('/');
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="project-selection">
      <header className="header">
        <div className="logo">LOGO</div>
        <div className="user-menu">
          <div className="user-avatar">
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <div className="user-dropdown">
            <span>Conectado como {user?.username}</span>
            <button onClick={logout}>Cerrar Sesión</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="page-header">
          <h1>Todos los proyectos</h1>
          <button 
            className="new-project-btn"
            onClick={() => setShowNewProjectForm(true)}
          >
            Nuevo proyecto
          </button>
        </div>

        <div className="search-filters">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="date-filter">
            <Calendar size={20} />
            <input type="date" />
          </div>
        </div>

        {showNewProjectForm && (
          <div className="new-project-form">
            <h3>Crear Nuevo Proyecto</h3>
            <form onSubmit={handleCreateProject}>
              <input
                type="text"
                placeholder="Nombre del proyecto"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                required
              />
              <div className="form-actions">
                <button type="submit" disabled={loading}>
                  {loading ? 'Creando...' : 'Crear Proyecto'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowNewProjectForm(false)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {showEditProjectForm && editingProject && (
          <div className="new-project-form">
            <h3>Editar Proyecto</h3>
            <form onSubmit={handleUpdateProject}>
              <input
                type="text"
                placeholder="Nombre del proyecto"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                required
              />
              <div className="form-actions">
                <button type="submit" disabled={loading}>
                  {loading ? 'Actualizando...' : 'Actualizar Proyecto'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEditProjectForm(false);
                    setEditingProject(null);
                    setEditProjectName('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="projects-table">
          <div className="table-header">
            <div>Título del proyecto</div>
            <div>Fecha</div>
            <div></div>
          </div>
          
          {filteredProjects.map(project => (
            <div key={project.id} className="table-row">
              <div 
                className="project-name"
                onClick={() => handleSelectProject(project)}
              >
                {project.name}
              </div>
              <div className="project-date">
                {new Date(project.created_at).toLocaleDateString('es-ES')}
              </div>
              <div className="project-actions">
                <button onClick={() => handleEditProject(project)}><Edit size={16} /></button>
                <button onClick={() => handleDeleteProject(project)}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="empty-state">
            <h3>No hay proyectos disponibles</h3>
            <p>Crea tu primer proyecto para comenzar</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default ProjectSelection;